/**
 * VsPredictiveIndexPage — the public /vs/predictive-index competitor
 * comparison page.
 *
 * Ported verbatim from the locked design
 * design/mockups/V1_Landing_VS_Predictive_Index_v1.html (Template 2 competitor
 * variant, source V1_Competitor_Comparison_Pages.md Page 2). Replaces the
 * noindex marketing scaffold for /vs/predictive-index with real, indexable
 * content.
 *
 * COMPETITOR CLAIMS ARE FACT-CHECK-GATED PRE-PUBLISH: every cell in the
 * Predictive Index column of the comparison table — and the competitor claims
 * in the hero, definition, fair-block, and FAQ — must be fact-checked against
 * Predictive Index's current site before publish; PI's feature set, the
 * Relationship Guide's current scope, and pricing change. Legal cleared the
 * competitor pages to mock (Russ, 2026-05-20).
 *
 * The app shell's <TopNav> renders the nav; this page is content + footer
 * (everything below the mockup's <nav class="marketing-nav">). Scoped <style>
 * block aliases the mockup's colors onto locked --ppr-* tokens; replicates the
 * locked pattern from PricingPage.jsx.
 */

import { Link } from "react-router-dom";
import { usePageMeta } from "../../lib/use-page-meta.js";

const VSPI_DESCRIPTION =
  "Predictive Index reads people through a behavioral assessment they have to take. PeoplePrints reads people from data you already have — no assessment, from $49/month. The honest comparison.";

// competitor claims are fact-check-gated pre-publish — verify every Predictive
// Index cell against PI's current site at publish time.
const COMPARISON_ROWS = [
  {
    feature: "How the read is generated",
    pp: "From minimal common data points already on file",
    rival: "From a behavioral assessment the person completes",
  },
  {
    feature: "Does the person have to do anything",
    pp: "No",
    rival: "Yes — they complete an assessment",
  },
  {
    feature: "Works on interview candidates mid-loop",
    pp: "Yes",
    rival: "Only if the candidate completes the assessment first",
  },
  {
    feature: "Works on employees in conflict",
    pp: "Yes",
    rival: "Requires both to have completed an assessment",
  },
  {
    feature: "Speed to a usable read",
    pp: "Instant",
    rival: "The assessment cycle",
  },
  {
    feature: "Conflict mediation surface",
    pp: "Yes — a first-class V1 feature",
    rival:
      "A Relationship Guide exists; not a conflict-mediation-specific surface",
  },
  {
    feature: "Claim register",
    pp: 'Hedge-disciplined ("tends to," "watch for")',
    rival: "Assessment-scored",
  },
  {
    feature: "Starting price",
    pp: "$49/month",
    rival: "Quote-based, enterprise-oriented",
  },
  {
    feature: "Rollout program required",
    pp: "No",
    rival: "Typically yes",
  },
];

const FAQS = [
  {
    q: "What's the main difference between PeoplePrints and Predictive Index?",
    a: "Predictive Index generates its behavioral read from an assessment the person completes. PeoplePrints generates a Predisposition Profile from minimal common data points already on file — no assessment required. That means PeoplePrints can read interview candidates, employees in conflict, and offer-stage candidates, who typically will not take an assessment.",
  },
  {
    q: "Is PeoplePrints cheaper than Predictive Index?",
    a: "PeoplePrints starts at $49/month with no rollout program. Predictive Index is quote-based and enterprise-oriented. For a single HR seat or a small team, PeoplePrints is substantially less expensive; verify PI's current pricing for your situation.",
  },
  {
    q: "Does PeoplePrints have published validation like Predictive Index?",
    a: "Not yet. Predictive Index has a longer track record and a more developed published validation base. PeoplePrints is a newer product with independent validation in progress; we publish what the data shows when there is enough of it to publish. The honest framing is at /methodology.",
  },
  {
    q: "Can I use PeoplePrints for conflict between two employees?",
    a: "Yes. The Conflict Mediation Blueprint generates a Profile for each party and one mediation sequence. It does not require either employee to complete an assessment.",
  },
  {
    q: "Is this a hire / no-hire score?",
    a: "No. PeoplePrints does not produce an applicant score or an automated decision. It tells you how to run the conversation; the decision stays with you.",
  },
];

// FAQPage structured data — derived from FAQS so the schema text stays
// verbatim-matched to the on-page FAQ (a Google requirement for FAQ rich
// results). Mirrors the locked pattern in PricingPage.jsx.
const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FOOTER_LINKS = [
  { to: "/#how-it-works", label: "Product" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/for/recruiters", label: "For Recruiters" },
  { to: "/for/managers", label: "For Managers" },
  { to: "/for/hr-directors", label: "For HR Directors" },
  { to: "/for/founders", label: "For Founders" },
  { to: "/for/employee-relations", label: "For Employee Relations" },
  { to: "/methodology", label: "Methodology" },
  { to: "/about", label: "About" },
  { to: "/security-and-privacy", label: "Security & Privacy" },
  { to: "/demo", label: "Request a Demo" },
];

export default function VsPredictiveIndexPage() {
  usePageMeta({
    title:
      "PeoplePrints vs. Predictive Index — A Predictive Index Alternative With No Assessment",
    description: VSPI_DESCRIPTION,
    canonicalPath: "/vs/predictive-index",
  });

  return (
    <div className="ppr-vspi-root">
      <style>{VSPI_CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      {/* ── BLOCK 2 — HERO ── */}
      <section className="ppr-vspi-hero">
        <div className="ppr-vspi-hero-inner">
          <p className="ppr-vspi-hero-eyebrow">
            PeoplePrints vs. Predictive Index
          </p>
          <h1 className="ppr-vspi-hero-h1">
            Looking for a Predictive Index alternative that doesn't require an
            assessment?
          </h1>
          <p className="ppr-vspi-hero-sub">
            Predictive Index is an established talent-assessment platform with
            a long track record. Its read depends on the person completing a
            behavioral assessment. PeoplePrints generates its read from minimal
            common data points you already have — no assessment, no waiting,
            and it works on candidates and employees who will never sit for a
            test. Here is the honest comparison.
          </p>
        </div>
      </section>

      {/* ── BLOCK 3 — EXTRACTABLE DEFINITION ── */}
      <section className="ppr-vspi-block ppr-vspi-compact">
        <div className="ppr-vspi-block-inner">
          <div className="ppr-vspi-definition">
            <p className="ppr-vspi-def-label">
              PeoplePrints as a Predictive Index alternative
            </p>
            <p className="ppr-vspi-def-text">
              PeoplePrints is a pre-interaction intelligence tool and an
              alternative to Predictive Index for HR users who need a
              behavioral read without requiring the person to complete an
              assessment. The core difference: Predictive Index generates its
              behavioral read from an assessment the person must take;
              PeoplePrints generates a{" "}
              <strong>Predisposition Profile</strong> from minimal common data
              points already on file, so it produces a read on interview
              candidates, employees in conflict, and offer-stage candidates —
              none of whom will typically take an assessment.
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOCK 4 — THE HONEST COMPARISON ── */}
      <section className="ppr-vspi-block ppr-vspi-compact">
        <div className="ppr-vspi-block-inner">
          <p className="ppr-vspi-block-eyebrow">The honest comparison</p>
          <h2 className="ppr-vspi-block-h2">
            A different version of the problem.
          </h2>
          <p className="ppr-vspi-block-body">
            Predictive Index is a serious platform. It has a long operating
            history, a published validation base, and a
            behavioral-plus-cognitive assessment methodology that many
            enterprises have standardized on. If your organization wants a
            deep, assessment-based behavioral program and has the budget and
            the rollout capacity for it, Predictive Index is a credible choice.
          </p>
          <p className="ppr-vspi-block-body">
            PeoplePrints solves a different version of the problem. The
            assessment requirement is the gating constraint: the interview
            candidate will not take an assessment mid-loop, the two employees
            in a conflict will not take one to help you mediate, and the
            offer-stage candidate has not taken one and will not before the
            offer conversation that determines whether they say yes.
            PeoplePrints reads people from data you already have, instantly, so
            it covers the pre-interaction moments an assessment-based platform
            structurally cannot reach. It is also a fraction of the cost and
            requires no rollout program.
          </p>
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="ppr-vspi-block ppr-vspi-compact ppr-vspi-tinted">
        <div className="ppr-vspi-block-inner">
          <p className="ppr-vspi-block-eyebrow">Side by side</p>
          <h2 className="ppr-vspi-block-h2">
            PeoplePrints vs. Predictive Index.
          </h2>
          {/* competitor claims are fact-check-gated pre-publish */}
          <table className="ppr-vspi-cmp-table">
            <thead>
              <tr>
                <th></th>
                <th className="ppr-vspi-col-pp">PeoplePrints</th>
                <th className="ppr-vspi-col-rival">Predictive Index</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.feature}>
                  <th>{row.feature}</th>
                  <td className="ppr-vspi-col-pp">{row.pp}</td>
                  <td className="ppr-vspi-col-rival">{row.rival}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ppr-vspi-cmp-note">
            FACT-CHECK before publish: every cell in the Predictive Index
            column. PI's feature set, the Relationship Guide's current scope,
            and pricing change; verify against PI's current site at publish
            time.
          </p>
        </div>
      </section>

      {/* ── WHERE THE COMPETITOR IS AHEAD ── */}
      <section className="ppr-vspi-block ppr-vspi-compact">
        <div className="ppr-vspi-block-inner">
          {/* competitor claims are fact-check-gated pre-publish */}
          <div className="ppr-vspi-fair-block">
            <p className="ppr-vspi-fb-label">
              Where Predictive Index is ahead
            </p>
            <p className="ppr-vspi-fb-body">
              To be fair about it: Predictive Index has a longer track record
              and a more developed published validation base than
              PeoplePrints, which is a newer product with independent
              validation still in progress. Predictive Index also offers a
              cognitive-assessment component and a deeper set of HRIS
              integrations than PeoplePrints' V1. If your organization wants a
              full assessment-based talent program and has the budget and
              rollout capacity, Predictive Index is built for that.
              PeoplePrints is built for the pre-interaction moments an
              assessment cannot reach —{" "}
              <strong>
                and we would rather tell you the honest tradeoff than oversell.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOCK 9 — FAQ ── */}
      <section className="ppr-vspi-block ppr-vspi-compact ppr-vspi-tinted">
        <div className="ppr-vspi-block-inner">
          <p className="ppr-vspi-block-eyebrow">FAQ</p>
          <h2 className="ppr-vspi-block-h2">
            PeoplePrints vs. Predictive Index — common questions.
          </h2>
          <div className="ppr-vspi-faq-list">
            {FAQS.map((f, i) => (
              <details className="ppr-vspi-faq-item" key={f.q} open={i === 0}>
                <summary className="ppr-vspi-faq-q">{f.q}</summary>
                <p className="ppr-vspi-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOCK 10 — CREDENTIALING STRIP ── */}
      <section className="ppr-vspi-cred-strip">
        <div className="ppr-vspi-cred-strip-inner">
          <span className="ppr-vspi-cs-label">The engine</span>
          PeoplePrints is built on a proprietary behavioral-prediction engine.
          The full methodology is at{" "}
          <Link to="/methodology">/methodology</Link>.
        </div>
      </section>

      {/* ── BLOCK 11 — CTA ── */}
      <section className="ppr-vspi-cta-band">
        <div className="ppr-vspi-cta-band-inner">
          <h2 className="ppr-vspi-cta-band-h">
            A Predictive Index alternative — no assessment required.
          </h2>
          <div className="ppr-vspi-cta-actions">
            <Link
              to="/demo"
              className="ppr-vspi-cta-btn ppr-vspi-cta-primary"
            >
              Request a demo
            </Link>
            <Link
              to="/for/hr-directors"
              className="ppr-vspi-cta-btn ppr-vspi-cta-secondary"
            >
              See PeoplePrints for HR Directors
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer — clean A6 nav ── */}
      <footer className="ppr-vspi-footer">
        <div className="ppr-vspi-footer-inner">
          <div className="ppr-vspi-footer-links">
            {FOOTER_LINKS.map((l) => (
              <Link to={l.to} key={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="ppr-vspi-footer-legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <p className="ppr-vspi-footer-copy">
            © 2026 PeoplePrints™. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

const VSPI_CSS = `
.ppr-vspi-root {
  --c-navy: var(--ppr-color-accent-navy);
  --c-steel: var(--ppr-color-accent-steel);
  --c-steel-dark: var(--ppr-color-accent-steel-dark);
  --c-powder: var(--ppr-color-accent-powder);
  --c-pale: var(--ppr-color-accent-pale);
  --c-pale-soft: var(--ppr-color-accent-pale-soft);
  --c-coral: var(--ppr-color-accent-coral);
  --c-coral-dark: var(--ppr-color-accent-coral-dark);
  --c-coral-very-soft: var(--ppr-color-accent-coral-very-soft);
  /* --c-gold: the locked marketing mockups use a gold hero-eyebrow accent
     with no token in design-tokens.v2.json — ported as a literal, flagged
     for design (same flag as PricingPage / the C1 homepage rebuild).
     gold-soft / gold-deep (fair-block) likewise have no token — derived
     literals, flagged for design alongside --c-gold. */
  --c-gold: #C99A4B;
  --c-gold-soft: #FBF6EC;
  --c-gold-deep: #8A6926;
  --c-surface: var(--ppr-color-background-surface);
  --c-text: var(--ppr-color-text-primary);
  --c-text-2: var(--ppr-color-text-secondary);
  --c-muted: var(--ppr-color-text-muted);
  --c-border: var(--ppr-color-border-default);
  --f-display: var(--ppr-typography-font-family-display);
  --f-body: var(--ppr-typography-font-family-body);
  --f-ui: var(--ppr-typography-font-family-ui);
  --f-mono: var(--ppr-typography-font-family-mono);
  font-family: var(--f-body);
  color: var(--c-text);
  line-height: 1.55;
  background: var(--c-surface);
}
.ppr-vspi-root * { box-sizing: border-box; }

.ppr-vspi-block { padding: 64px 32px; }
.ppr-vspi-block.ppr-vspi-compact { padding: 44px 32px; }
.ppr-vspi-block.ppr-vspi-tinted { background: var(--c-pale-soft); }
.ppr-vspi-block-inner { max-width: 940px; margin: 0 auto; }
.ppr-vspi-block-eyebrow { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 14px; }
.ppr-vspi-block-h2 { font-family: var(--f-display); font-size: 27px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 16px; }
.ppr-vspi-block-body { font-family: var(--f-body); font-size: 16.5px; line-height: 1.65; color: var(--c-text); margin: 0 0 16px; }
.ppr-vspi-block-body strong { color: var(--c-navy); }

.ppr-vspi-hero { padding: 80px 32px 56px; background: linear-gradient(180deg, var(--c-pale-soft) 0%, var(--c-surface) 100%); }
.ppr-vspi-hero-inner { max-width: 900px; margin: 0 auto; text-align: center; }
.ppr-vspi-hero-eyebrow { font-family: var(--f-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--c-gold); margin: 0 0 18px; }
.ppr-vspi-hero-h1 { font-family: var(--f-display); font-size: 38px; line-height: 1.1; letter-spacing: -0.025em; color: var(--c-navy); margin: 0 0 20px; }
.ppr-vspi-hero-sub { font-family: var(--f-body); font-size: 18px; line-height: 1.65; color: var(--c-text-2); margin: 0 auto; max-width: 720px; text-align: left; }

.ppr-vspi-definition { background: var(--c-pale-soft); border: 1.5px solid var(--c-powder); border-left: 6px solid var(--c-navy); padding: 26px 30px; }
.ppr-vspi-def-label { font-family: var(--f-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--c-steel-dark); margin: 0 0 10px; }
.ppr-vspi-def-text { font-family: var(--f-body); font-size: 17px; line-height: 1.65; color: var(--c-text); margin: 0; }
.ppr-vspi-def-text strong { color: var(--c-navy); }

.ppr-vspi-cmp-table { width: 100%; border-collapse: collapse; margin: 20px 0 0; font-family: var(--f-ui); font-size: 13.5px; }
.ppr-vspi-cmp-table th, .ppr-vspi-cmp-table td { text-align: left; padding: 12px 16px; border-bottom: 1px solid var(--c-border); vertical-align: top; }
.ppr-vspi-cmp-table thead th { font-family: var(--f-display); font-size: 12px; letter-spacing: 0.04em; }
.ppr-vspi-cmp-table thead th.ppr-vspi-col-pp { color: var(--c-coral-dark); border-bottom: 2px solid var(--c-coral); }
.ppr-vspi-cmp-table thead th.ppr-vspi-col-rival { color: var(--c-steel-dark); border-bottom: 2px solid var(--c-border); }
.ppr-vspi-cmp-table tbody th { font-family: var(--f-ui); font-weight: 700; color: var(--c-navy); width: 30%; }
.ppr-vspi-cmp-table td.ppr-vspi-col-pp { color: var(--c-text); background: var(--c-coral-very-soft); }
.ppr-vspi-cmp-table td.ppr-vspi-col-rival { color: var(--c-text-2); }
.ppr-vspi-cmp-note { font-family: var(--f-mono); font-size: 10.5px; color: var(--c-muted); margin: 12px 0 0; line-height: 1.5; }

.ppr-vspi-fair-block { background: var(--c-gold-soft); border: 1.5px solid var(--c-gold); padding: 22px 26px; }
.ppr-vspi-fb-label { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.10em; text-transform: uppercase; color: var(--c-gold-deep); margin: 0 0 8px; }
.ppr-vspi-fb-body { font-family: var(--f-body); font-size: 15.5px; line-height: 1.65; color: var(--c-text); margin: 0; }
.ppr-vspi-fb-body strong { color: var(--c-navy); }

.ppr-vspi-faq-list { margin: 20px 0 0; }
.ppr-vspi-faq-item { border: 1.5px solid var(--c-border); border-left: 4px solid var(--c-steel); margin: 0 0 12px; background: var(--c-surface); padding: 20px 24px; }
.ppr-vspi-faq-q { font-family: var(--f-display); font-size: 15.5px; color: var(--c-navy); margin: 0 0 8px; letter-spacing: -0.005em; cursor: pointer; list-style: none; }
.ppr-vspi-faq-q::-webkit-details-marker { display: none; }
.ppr-vspi-faq-item[open] .ppr-vspi-faq-q { margin-bottom: 8px; }
.ppr-vspi-faq-a { font-family: var(--f-body); font-size: 15px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-vspi-faq-a strong { color: var(--c-navy); }

.ppr-vspi-cred-strip { background: var(--c-navy); color: #FFF; padding: 28px 32px; }
.ppr-vspi-cred-strip-inner { max-width: 940px; margin: 0 auto; font-family: var(--f-body); font-size: 14.5px; line-height: 1.65; }
.ppr-vspi-cred-strip-inner a { color: var(--c-powder); text-decoration: underline; }
.ppr-vspi-cs-label { font-family: var(--f-display); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-powder); display: block; margin: 0 0 8px; }

.ppr-vspi-cta-band { background: var(--c-pale-soft); padding: 60px 32px; text-align: center; }
.ppr-vspi-cta-band-inner { max-width: 760px; margin: 0 auto; }
.ppr-vspi-cta-band-h { font-family: var(--f-display); font-size: 24px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 24px; }
.ppr-vspi-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
.ppr-vspi-cta-btn { display: inline-block; padding: 14px 26px; border-radius: 6px; text-decoration: none; font-family: var(--f-display); font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; }
.ppr-vspi-cta-primary { background: var(--c-coral); color: #FFF; border: 2px solid var(--c-coral); }
.ppr-vspi-cta-secondary { background: transparent; color: var(--c-navy); border: 2px solid var(--c-navy); }

.ppr-vspi-footer { background: var(--c-surface); border-top: 1px solid var(--c-border); padding: 44px 32px; }
.ppr-vspi-footer-inner { max-width: 1080px; margin: 0 auto; text-align: center; }
.ppr-vspi-footer-links { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin: 0 0 14px; font-family: var(--f-ui); font-size: 13px; font-weight: 600; }
.ppr-vspi-footer-links a { color: var(--c-text-2); text-decoration: none; }
.ppr-vspi-footer-legal { display: flex; gap: 16px; justify-content: center; margin: 0 0 16px; font-family: var(--f-ui); font-size: 12.5px; }
.ppr-vspi-footer-legal a { color: var(--c-muted); text-decoration: none; }
.ppr-vspi-footer-copy { font-family: var(--f-mono); font-size: 11.5px; color: var(--c-muted); }

@media (max-width: 800px) {
  .ppr-vspi-hero-h1 { font-size: 28px; }
  .ppr-vspi-block-h2 { font-size: 22px; }
  .ppr-vspi-cmp-table { font-size: 12px; }
  .ppr-vspi-cmp-table th, .ppr-vspi-cmp-table td { padding: 9px 10px; }
}
`;
